import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, UserPlus, Paperclip, Send, Users } from 'lucide-react';
import { toast } from 'sonner';
import { aiAgentApi } from '../../../lib/aiAgentApi';
import { getCurrentAccount } from '../../../lib/auth';
import { MessageList } from '../MessageList';
import { InvitePicker } from './InvitePicker';

/**
 * Full-screen chat detail view (within the drawer). Header shows the chat
 * title and an invite-teammate button (+). Bottom is composer with paperclip
 * attach and send arrow per wireframe.
 */
export const ChatDetail = ({ conv, onBack, onChanged }) => {
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [collabCount, setCollabCount] = useState(conv?.collaborator_user_ids?.length || 0);
  const [draft, setDraft] = useState('');
  const [attachments, setAttachments] = useState([]); // [{file_id, s3_key, filename, content_type, size_bytes}]

  // Auto-grow textarea: min 100px, max 300px (then scroll).
  const taRef = useRef(null);
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    const next = Math.min(Math.max(ta.scrollHeight, 100), 300);
    ta.style.height = next + 'px';
  }, [draft]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalMessages, setTotalMessages] = useState(0);

  const currentAccount = useMemo(() => getCurrentAccount(), []);
  const accountId = currentAccount?.id || null;

  // Load the LATEST page of messages on open (page size = 5).
  useEffect(() => {
    if (!conv?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const detail = await aiAgentApi.getConversation(conv.id, { limit: 5, include_tool: false });
        if (cancelled) return;
        setMessages(detail.messages || []);
        setCollabCount(detail.conversation?.collaborator_user_ids?.length || 0);
        setHasMore(Boolean(detail.has_more));
        setTotalMessages(detail.total_messages || (detail.messages || []).length);
      } catch {
        if (!cancelled) toast.error('Could not open this chat');
      }
    })();
    return () => { cancelled = true; };
  }, [conv?.id]);

  // Scroll-back: load 10 older messages and prepend.
  const loadOlder = useCallback(async () => {
    if (!conv?.id || loadingMore || !hasMore) return;
    const oldestTs = messages.length > 0 ? messages[0].ts : undefined;
    if (!oldestTs) return;
    setLoadingMore(true);
    try {
      const detail = await aiAgentApi.getConversation(conv.id, {
        limit: 10, before_ts: oldestTs, include_tool: false,
      });
      const older = detail.messages || [];
      // De-dupe in case server returns boundary overlap
      const seen = new Set(messages.map((m) => m.id));
      const merged = [...older.filter((m) => !seen.has(m.id)), ...messages];
      setMessages(merged);
      setHasMore(Boolean(detail.has_more));
    } catch {
      toast.error('Could not load earlier messages');
    } finally {
      setLoadingMore(false);
    }
  }, [conv?.id, hasMore, loadingMore, messages]);

  const send = useCallback(async (content) => {
    if (!conv?.id) return;
    const userMsg = { id: `local_${Date.now()}`, role: 'user', content, ts: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg]);
    setBusy(true);
    try {
      const result = await aiAgentApi.sendMessage(conv.id, content, accountId);
      const trace = result.tool_trace || [];
      const traceMsgs = trace.map((t, i) => ({
        id: `tool_${Date.now()}_${i}`,
        role: 'tool',
        content: JSON.stringify(t),
        tool_name: t.name,
        ts: new Date().toISOString(),
      }));
      const final = {
        id: `assistant_${Date.now()}`,
        role: 'assistant',
        content: result.final || '',
        report_job: result.report_job || null,
        suggested_actions: result.suggested_actions || [],
        ts: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, ...traceMsgs, final]);
      onChanged?.();
    } catch {
      toast.error('Sorry — the agent ran into a problem');
    } finally {
      setBusy(false);
    }
  }, [conv?.id, accountId, onChanged]);

  const onAttach = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File exceeds 25MB');
      return;
    }
    try {
      const pre = await aiAgentApi.presignUpload({
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
        size_bytes: file.size,
        conv_id: conv?.id,
        visibility: 'private',
      });
      const putRes = await fetch(pre.upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': pre.content_type },
        body: file,
      });
      if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
      const confirmed = await aiAgentApi.confirmUpload({
        file_id: pre.file_id,
        s3_key: pre.s3_key,
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
        size_bytes: file.size,
        conv_id: conv?.id,
        visibility: 'private',
      });
      setAttachments((prev) => [...prev, confirmed]);
      toast.success(`Attached ${file.name}`);
    } catch (e) {
      toast.error(e?.message || 'Upload failed');
    }
  };

  const onSendClick = () => {
    const txt = draft.trim();
    if (!txt || busy) return;
    let content = txt;
    if (attachments.length) {
      // Include each file_id inline so the agent can call analyze_document
      // without having to first run list_drive_files.
      const lines = attachments.map((a) =>
        `  - ${a.filename} (file_id: ${a.file_id})`).join('\n');
      content += `\n\n[Attached ${attachments.length} file(s) from AI Agent Drive — call analyze_document on each:\n${lines}\n]`;
    }
    send(content);
    setDraft('');
    setAttachments([]);
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSendClick();
    }
  };

  const title = conv?.title?.trim() || 'New chat';

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2.5">
        <button
          data-testid="ai-chat-back"
          onClick={onBack}
          className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
          aria-label="Back to chats"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900">{title}</div>
        <button
          data-testid="ai-invite-open"
          onClick={() => setShowInvite(true)}
          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:border-emerald-300 hover:text-emerald-700"
          title="Invite teammates"
        >
          <UserPlus className="h-3.5 w-3.5" />
          {collabCount > 0 ? <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />{collabCount}</span> : <span>Invite</span>}
        </button>
      </div>

      <div className="flex-1 min-h-0 flex flex-col">
        {hasMore && (
          <div className="border-b border-slate-100 bg-white px-4 py-1.5 text-center">
            <button
              data-testid="ai-chat-load-older"
              onClick={loadOlder}
              disabled={loadingMore}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
            >
              {loadingMore ? 'Loading earlier messages…' : (
                `Load earlier messages (${Math.max(0, totalMessages - messages.length)} remaining)`
              )}
            </button>
          </div>
        )}
        <MessageList messages={messages} busy={busy} statusTool="thinking" onSuggestedAction={send} />
      </div>

      {/* Attachment chips */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-1.5 border-t border-slate-100 px-3 py-2">
          {attachments.map((a) => (
            <span key={a.id} className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700">
              <Paperclip className="h-3 w-3" />{a.filename}
              <button onClick={() => setAttachments((prev) => prev.filter((x) => x.id !== a.id))} className="ml-0.5 text-slate-400 hover:text-rose-600">×</button>
            </span>
          ))}
        </div>
      )}

      {/* Composer (paperclip + send arrow per wireframe) */}
      <div className="border-t border-slate-100 bg-white p-3" data-testid="ai-chat-composer">
        <div className="flex items-end gap-2">
          <textarea
            ref={taRef}
            data-testid="ai-chat-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKey}
            rows={1}
            placeholder="Ask anything about your data or to draft a vendor email…"
            disabled={busy}
            className="flex-1 resize-none rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none transition focus:border-emerald-300 focus:bg-white focus:ring-2 focus:ring-emerald-100 overflow-y-auto"
            style={{ minHeight: 100, maxHeight: 300 }}
          />
          <label
            data-testid="ai-chat-attach"
            className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-xl border border-slate-200 text-slate-500 hover:border-emerald-300 hover:text-emerald-700 ${busy ? 'opacity-40 pointer-events-none' : ''}`}
            title="Attach file"
          >
            <Paperclip className="h-4 w-4" />
            <input
              type="file"
              className="hidden"
              onChange={(e) => { onAttach(e.target.files?.[0]); e.target.value = ''; }}
            />
          </label>
          <button
            data-testid="ai-chat-send"
            onClick={onSendClick}
            disabled={busy || (!draft.trim() && attachments.length === 0)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
            aria-label="Send"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>

      {showInvite && conv?.id && (
        <InvitePicker
          convId={conv.id}
          onClose={() => setShowInvite(false)}
          onChanged={() => onChanged?.()}
        />
      )}
    </div>
  );
};
